import { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Users, Target, DollarSign, UserCheck, History } from 'lucide-react'
import {
  Bar, BarChart, CartesianGrid, Cell, LabelList,
  ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  useConfiguracionFidelizacion,
  useMetadataFidelizacion,
  useMetricasPorCampo,
  useTodosParticipantesFidelizacion,
} from '../hooks/useFidelizacion'
import { colorHex, kpiGradientClasses, kpiIconClasses, normalizeColor, shadesForBars } from '../lib/reporteColor'
import { CAMPO_REPRESENTANTE_CANDIDATES, collectPairs, pickCampo, pickCampoFiltrable, parseMulti, joinMulti } from '../lib/campos'
import { derivarJefesYMedicos, filtrarRepresentantesPorJefe } from '../lib/representantesMedifarmaPeru'
import { parseNumeric } from '../components/charts/palette'
import { formatMoneda, formatNumero } from '../lib/paisMapa'
import { ReporteHeader } from '../components/ReporteHeader'
import { ReporteSubNav } from '../components/ReporteSubNav'
import { MultiSearchableSelect } from '../components/MultiSearchableSelect'
import { MonthFilter } from '../components/MonthFilter'
import { usePeriodoReporte } from '../hooks/usePeriodoReporte'
import { BarChartRepresentante, type RepresentanteDatum } from '../components/charts/BarChartRepresentante'
import { ParticipantesActivosPage } from './ParticipantesActivosPage'

const META_PUNTOS = 300
const VALOR_PUNTO = 0.10
const COLOR_BAJO_META = '#858796'

interface ParticipantePuntos {
  nombre: string
  puntos: number
  superaMeta: boolean
  fill: string
}

// created_at llega como ISO UTC. Convertir al timezone del programa antes de
// comparar para evitar que registros de las últimas horas del día caigan en el día siguiente.
const TZ_PERU = 'America/Lima'
function toLocalDate(iso: string): string {
  const d = new Date(iso)
  if (isNaN(d.getTime())) return iso.slice(0, 10)
  return new Intl.DateTimeFormat('en-CA', { timeZone: TZ_PERU, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d)
}

/** True si la fecha ISO `created_at` cae dentro del rango [desde, hasta] (inclusive). */
function enRango(createdAt: unknown, desde: string, hasta: string): boolean {
  if (typeof createdAt !== 'string' || createdAt.length < 10) return false
  const dia = toLocalDate(createdAt)
  return dia >= desde && dia <= hasta
}

/**
 * Dispatcher del tab Participantes: cada reporte tiene una vista distinta.
 * - Medifarma Perú (id 7): participantes activos por área (`ParticipantesActivosPage`).
 * - Resto (Sanfer, id 6): vista de puntos vs. meta (`ParticipantesSanferView`).
 */
export function ParticipantesFidelizacionPage() {
  const { id } = useParams<{ id: string }>()
  const configId = id ? parseInt(id, 10) : 0
  if (configId === 7) return <ParticipantesActivosPage />
  return <ParticipantesSanferView />
}

function ParticipantesSanferView() {
  const { id } = useParams<{ id: string }>()
  const configId = id ? parseInt(id, 10) : 0

  const config = useConfiguracionFidelizacion(configId)
  const metadata = useMetadataFidelizacion(configId)
  const todosParticipantes = useTodosParticipantesFidelizacion(configId)

  // Vista interna: gráfica actual de puntos vs. desglose por representante.
  const [vista, setVista] = useState<'participante' | 'representante'>('participante')

  // Filtros de la sección (aplican a ambas vistas). Por defecto se muestra todo
  // el histórico (comportamiento previo de la página); el usuario puede acotar a
  // un mes o a un rango de fechas personalizado.
  const {
    mes,
    setMes,
    sinFecha,
    setSinFecha,
    modo,
    setModo,
    rangoPersonalizado,
    setRangoPersonalizado,
    rango,
    rangoEfectivo,
  } = usePeriodoReporte({ sinFechaInicial: true })
  // Representante(s) y cascada son multi-selección: se guardan como arrays.
  const [reps, setReps] = useState<string[]>([])
  // Filtros cascada (Medifarma Perú, id 7): solo acotan las opciones de Representante.
  const [jefesVentas, setJefesVentas] = useState<string[]>([])
  const [repsMedico, setRepsMedico] = useState<string[]>([])

  const camposExternos = metadata.data?.campos_externos
  // `agrupable` para el endpoint de métricas; `filtrable` para leer datos_extra y poblar el dropdown.
  const campoRep = useMemo(
    () => pickCampo(camposExternos, CAMPO_REPRESENTANTE_CANDIDATES),
    [camposExternos],
  )
  const campoRepFiltro = useMemo(
    () => pickCampoFiltrable(camposExternos, CAMPO_REPRESENTANTE_CANDIDATES),
    [camposExternos],
  )
  const repKey = campoRepFiltro?.key ?? campoRep?.key ?? null

  // Opciones {id, nombre} para el selector de representante, desde el listado completo.
  const repOptions = useMemo(() => {
    if (!repKey) return []
    return collectPairs(
      todosParticipantes.data ?? [],
      (p) => (p.datos_extra as Record<string, unknown> | undefined)?.[repKey],
      (p) => (p.datos_extra as Record<string, unknown> | undefined)?.[`${repKey}_nombre`],
    )
  }, [todosParticipantes.data, repKey])

  // ---- Cascada Jefe de Ventas / Representante Médico (solo Medifarma Perú) ----
  const { opcionesJefe, opcionesMedico } = useMemo(
    () => derivarJefesYMedicos(configId, repOptions),
    [configId, repOptions],
  )
  const repOptionsCascada = useMemo(
    () => filtrarRepresentantesPorJefe(configId, repOptions, jefesVentas, repsMedico),
    [configId, repOptions, jefesVentas, repsMedico],
  )
  // Al cambiar la cascada, quitar de los Representantes elegidos los que ya no pertenezcan.
  const aplicarCascada = (jefes: string[], sups: string[]) => {
    setJefesVentas(jefes)
    setRepsMedico(sups)
    if (reps.length > 0) {
      const permitidos = new Set(
        filtrarRepresentantesPorJefe(configId, repOptions, jefes, sups).map((o) => o.value),
      )
      const next = reps.filter((r) => permitidos.has(r))
      if (next.length !== reps.length) setReps(next)
    }
  }
  // Conjunto de IDs de representante del jefe/supervisor elegido; se usa para filtrar
  // client-side salvo que haya representante(s) puntual(es) (que tienen prioridad).
  const repSetCascada = useMemo(() => {
    if (configId !== 7 || reps.length > 0 || (jefesVentas.length === 0 && repsMedico.length === 0))
      return null
    return new Set(
      filtrarRepresentantesPorJefe(configId, repOptions, jefesVentas, repsMedico).map((o) => o.value),
    )
  }, [configId, reps, jefesVentas, repsMedico, repOptions])

  const color = normalizeColor(config.data)
  const colorPrincipal = colorHex(color.nombre, color.tono)
  const gradient = kpiGradientClasses(color.nombre)
  const c = kpiIconClasses(color.nombre, color.tono)
  const shades = shadesForBars(color.nombre).slice(0, 4) // 700→400, evitar tonos muy claros

  // ---- Vista "Por participante": filtrado client-side (representante + mes) ----
  const participantes = useMemo<ParticipantePuntos[]>(() => {
    if (!todosParticipantes.data) return []
    const items = todosParticipantes.data
      .filter((p) => {
        if (reps.length > 0 && repKey) {
          const repId = (p.datos_extra as Record<string, unknown> | undefined)?.[repKey]
          if (!reps.includes(String(repId ?? ''))) return false
        }
        if (repSetCascada && repKey) {
          const repId = (p.datos_extra as Record<string, unknown> | undefined)?.[repKey]
          if (!repSetCascada.has(String(repId ?? ''))) return false
        }
        if (!sinFecha && !enRango(p.created_at, rango.desde, rango.hasta)) return false
        return true
      })
      .map((p) => {
        const puntos = parseFloat(String(p.puntos_disponibles ?? '0')) || 0
        return {
          nombre: `${String(p.nombre ?? '')} ${String(p.apellido ?? '')}`.trim(),
          puntos,
          superaMeta: puntos >= META_PUNTOS,
          fill: '',
        }
      })
      .filter((p) => p.puntos > 0)
      .sort((a, b) => b.puntos - a.puntos)

    // Degradado oscuro→claro sobre los que superan la meta; gris para los demás
    const sobreMetaItems = items.filter((p) => p.superaMeta)
    const bajMetaItems = items.filter((p) => !p.superaMeta)

    sobreMetaItems.forEach((p, i) => {
      const ratio = sobreMetaItems.length <= 1 ? 0 : i / (sobreMetaItems.length - 1)
      const colorIdx = Math.round(ratio * (shades.length - 1))
      p.fill = shades[colorIdx]
    })
    bajMetaItems.forEach((p) => {
      p.fill = COLOR_BAJO_META
    })

    return items
  }, [todosParticipantes.data, shades, reps, repKey, sinFecha, rango, repSetCascada])

  const sobreMeta = useMemo(() => participantes.filter((p) => p.superaMeta), [participantes])
  const totalPuntosSobreMeta = useMemo(
    () => sobreMeta.reduce((acc, p) => acc + p.puntos, 0),
    [sobreMeta],
  )

  const BAR_HEIGHT = 36
  const chartHeight = Math.max(participantes.length * BAR_HEIGHT + 20, 200)

  // ---- Vista "Por representante": métricas agrupadas del upstream ----
  const porRepresentante = useMetricasPorCampo(configId, campoRep?.key ?? null, {
    ...rangoEfectivo,
    top: 100,
  })

  const repChartData = useMemo<RepresentanteDatum[]>(
    () =>
      (porRepresentante.data?.datos ?? []).map((d) => ({
        label: d.valor,
        registros: d.participantes ?? 0,
        ventas: parseNumeric(d.monto),
      })),
    [porRepresentante.data],
  )

  const handleRefresh = () => {
    config.refetch()
    metadata.refetch()
    todosParticipantes.refetch()
    porRepresentante.refetch()
  }

  const refreshing =
    config.isFetching ||
    metadata.isFetching ||
    todosParticipantes.isFetching ||
    porRepresentante.isFetching

  return (
    <div className="p-6 space-y-6">
      <ReporteHeader
        config={config.data}
        seccion="Participantes"
        icon={Users}
        onRefresh={handleRefresh}
        refreshing={refreshing}
      />
      <ReporteSubNav configId={configId} colorNombre={color.nombre} colorTono={color.tono} />

      {/* Filtros de la sección: mes + representante. El selector de representante se
          oculta en la vista "Por representante" (ya desglosa por representante). */}
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs">Periodo</Label>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant={sinFecha ? 'default' : 'outline'}
                className={
                  sinFecha
                    ? `bg-${color.nombre}-700 hover:bg-${color.nombre}-800 text-white`
                    : 'border-gray-300 dark:border-gray-700'
                }
                onClick={() => setSinFecha(!sinFecha)}
              >
                <History className="h-4 w-4" />
                Todo el histórico
              </Button>
              {/* Cuando "Todo el histórico" está activo, el selector se atenúa y
                  desactiva (toggle Mes/Rango + control de fecha). */}
              <div className={`flex items-center gap-2 ${sinFecha ? 'opacity-50 pointer-events-none' : ''}`}>
                <div className="flex items-center rounded-md border border-gray-300 dark:border-gray-700 p-0.5">
                  {(['mes', 'rango'] as const).map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setModo(m)}
                      className={`rounded px-2 py-0.5 text-xs font-medium transition-colors ${
                        modo === m
                          ? `bg-${color.nombre}-700 text-white`
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      {m === 'mes' ? 'Mes' : 'Rango'}
                    </button>
                  ))}
                </div>
                {modo === 'rango' ? (
                  <div className="flex items-center gap-1.5">
                    <Input
                      type="date"
                      value={rangoPersonalizado.desde || ''}
                      min={metadata.data?.rango_datos?.primera_factura ?? undefined}
                      max={rangoPersonalizado.hasta || metadata.data?.rango_datos?.ultima_factura || undefined}
                      onChange={(e) =>
                        setRangoPersonalizado({ ...rangoPersonalizado, desde: e.target.value || undefined })
                      }
                      className="h-9 w-[9.5rem]"
                      aria-label="Desde"
                    />
                    <span className="text-xs text-muted-foreground">→</span>
                    <Input
                      type="date"
                      value={rangoPersonalizado.hasta || ''}
                      min={rangoPersonalizado.desde || metadata.data?.rango_datos?.primera_factura || undefined}
                      max={metadata.data?.rango_datos?.ultima_factura ?? undefined}
                      onChange={(e) =>
                        setRangoPersonalizado({ ...rangoPersonalizado, hasta: e.target.value || undefined })
                      }
                      className="h-9 w-[9.5rem]"
                      aria-label="Hasta"
                    />
                  </div>
                ) : (
                  <MonthFilter
                    value={mes}
                    onChange={setMes}
                    minDate={metadata.data?.rango_datos?.primera_factura ?? null}
                    maxDate={metadata.data?.rango_datos?.ultima_factura ?? null}
                  />
                )}
              </div>
            </div>
          </div>

          {vista === 'participante' && configId === 7 && opcionesJefe.length > 0 && (
            <div className="space-y-1.5 min-w-[220px]">
              <Label htmlFor="f-jefe-ventas" className="text-xs">Jefe de Ventas</Label>
              <MultiSearchableSelect
                id="f-jefe-ventas"
                placeholder="Todos"
                options={opcionesJefe}
                value={joinMulti(jefesVentas)}
                onChange={(v) => aplicarCascada(parseMulti(v), repsMedico)}
              />
            </div>
          )}

          {vista === 'participante' && configId === 7 && opcionesMedico.length > 0 && (
            <div className="space-y-1.5 min-w-[220px]">
              <Label htmlFor="f-rep-medico" className="text-xs">Supervisor</Label>
              <MultiSearchableSelect
                id="f-rep-medico"
                placeholder="Todos"
                options={opcionesMedico}
                value={joinMulti(repsMedico)}
                onChange={(v) => aplicarCascada(jefesVentas, parseMulti(v))}
              />
            </div>
          )}

          {vista === 'participante' && repOptions.length > 0 && (
            <div className="space-y-1.5 min-w-[220px]">
              <Label htmlFor="f-representante" className="text-xs">
                {campoRepFiltro?.label ?? campoRep?.label ?? 'Representante'}
              </Label>
              <MultiSearchableSelect
                id="f-representante"
                placeholder="Todos"
                options={repOptionsCascada}
                value={joinMulti(reps)}
                onChange={(v) => setReps(parseMulti(v))}
              />
            </div>
          )}
        </div>
      </div>

      <Tabs value={vista} onValueChange={(v) => setVista(v as 'participante' | 'representante')} className="w-full">
        <TabsList className="grid w-full h-auto grid-cols-2 p-1 bg-muted">
          <TabsTrigger value="participante" className="flex items-center gap-2 py-2.5 data-[state=active]:bg-background data-[state=active]:shadow-sm">
            <Target className="h-4 w-4" />
            <span className="text-sm font-medium">Por participante</span>
          </TabsTrigger>
          <TabsTrigger value="representante" className="flex items-center gap-2 py-2.5 data-[state=active]:bg-background data-[state=active]:shadow-sm">
            <UserCheck className="h-4 w-4" />
            <span className="text-sm font-medium">Por representante</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="participante" className="space-y-6 mt-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="border border-gray-300 dark:border-gray-700">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Participantes con puntos</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                {todosParticipantes.isLoading ? (
                  <Skeleton className="h-8 w-24" />
                ) : (
                  <div className="text-2xl font-bold text-black dark:text-white">
                    {formatNumero(participantes.length, configId)}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className={`border border-gray-300 dark:border-gray-700 ${gradient}`}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Superaron meta (≥{META_PUNTOS} pts)
                </CardTitle>
                <Target className={`h-4 w-4 ${c.icon}`} />
              </CardHeader>
              <CardContent>
                {todosParticipantes.isLoading ? (
                  <Skeleton className="h-8 w-24" />
                ) : (
                  <>
                    <div className="text-2xl font-bold text-black dark:text-white">
                      {formatNumero(sobreMeta.length, configId)}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      de {participantes.length} participantes
                    </p>
                  </>
                )}
              </CardContent>
            </Card>

            <Card className={`border border-gray-300 dark:border-gray-700 ${gradient}`}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Valor puntos sobre meta</CardTitle>
                <DollarSign className={`h-4 w-4 ${c.icon}`} />
              </CardHeader>
              <CardContent>
                {todosParticipantes.isLoading ? (
                  <Skeleton className="h-8 w-24" />
                ) : (
                  <>
                    <div className="text-2xl font-bold text-black dark:text-white">
                      ${formatNumero(totalPuntosSobreMeta * VALOR_PUNTO, configId, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {formatNumero(totalPuntosSobreMeta, configId)} pts × $0.10
                    </p>
                  </>
                )}
              </CardContent>
            </Card>
          </div>

          <Card className="border border-gray-300 dark:border-gray-700">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <div className="flex items-center gap-2">
                <Target className={`h-4 w-4 ${c.icon}`} />
                <CardTitle className="text-sm font-medium">Puntos por participante</CardTitle>
              </div>
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <span
                  className="inline-block h-2.5 w-2.5 rounded-sm"
                  style={{ background: colorPrincipal }}
                />
                Sobre meta
                <span
                  className="ml-2 inline-block h-2.5 w-2.5 rounded-sm"
                  style={{ background: COLOR_BAJO_META }}
                />
                Bajo meta
              </span>
            </CardHeader>
            <CardContent>
              {todosParticipantes.isLoading ? (
                <Skeleton className="h-[400px] w-full" />
              ) : participantes.length === 0 ? (
                <div className="flex items-center justify-center h-[200px] text-muted-foreground text-sm">
                  Sin datos disponibles
                </div>
              ) : (
                <div className="overflow-y-auto" style={{ maxHeight: '540px' }}>
                  <ResponsiveContainer width="100%" height={chartHeight}>
                    <BarChart
                      data={participantes}
                      layout="vertical"
                      margin={{ left: 0, right: 64, top: 4, bottom: 4 }}
                    >
                      <CartesianGrid
                        horizontal={false}
                        strokeDasharray="3 3"
                        stroke="hsl(var(--border))"
                      />
                      <YAxis
                        dataKey="nombre"
                        type="category"
                        tickLine={false}
                        tickMargin={8}
                        axisLine={false}
                        width={150}
                        tick={{ fontSize: 11 }}
                        interval={0}
                      />
                      <XAxis
                        type="number"
                        tickLine={false}
                        axisLine={false}
                        tick={{ fontSize: 11 }}
                      />
                      <Tooltip
                        formatter={(value: number) => [
                          formatNumero(value, configId),
                          'Puntos disponibles',
                        ]}
                        cursor={{ fill: 'hsl(var(--muted))', opacity: 0.5 }}
                      />
                      <ReferenceLine
                        x={META_PUNTOS}
                        stroke={colorPrincipal}
                        strokeWidth={2}
                        strokeDasharray="4 4"
                        label={{
                          value: `Meta ${META_PUNTOS}`,
                          position: 'insideTopRight',
                          fontSize: 11,
                          fill: colorPrincipal,
                        }}
                      />
                      <Bar dataKey="puntos" radius={[0, 4, 4, 0]}>
                        {participantes.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                        <LabelList
                          dataKey="puntos"
                          position="right"
                          style={{ fontSize: 11 }}
                          formatter={(v: number) => formatNumero(v, configId)}
                        />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="representante" className="mt-6">
          <BarChartRepresentante
            title="Registros y ventas por representante"
            data={repChartData}
            colorNombre={color.nombre}
            isLoading={porRepresentante.isLoading}
            error={porRepresentante.error}
            formatVentas={(v) => formatMoneda(v, configId, { maximumFractionDigits: 0 })}
            configId={configId}
          />
        </TabsContent>
      </Tabs>
    </div>
  )
}
